"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

import { cn } from "@/lib/utils";

interface StarButtonProps {
  collectionId: string;
  initialStarCount: number;
  initialHasStarred: boolean;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  showCount?: boolean;
  isAuthenticated?: boolean;
  onStarChange?: (starred: boolean) => void;
  className?: string;
}

export function StarButton({
  collectionId,
  initialStarCount,
  initialHasStarred,
  variant = "outline",
  size = "sm",
  showCount = true,
  isAuthenticated = false,
  onStarChange,
  className,
}: StarButtonProps) {
  const [starCount, setStarCount] = useState(initialStarCount);
  const [hasStarred, setHasStarred] = useState(initialHasStarred);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleStarToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Require authentication
    if (!isAuthenticated) {
      toast.error("Please sign in to star collections");
      router.push("/sign-in");
      return;
    }

    // Optimistic UI update
    const previousStarCount = starCount;
    const previousHasStarred = hasStarred;

    const newHasStarred = !hasStarred;
    const newStarCount = newHasStarred
      ? starCount + 1
      : Math.max(0, starCount - 1);

    setHasStarred(newHasStarred);
    setStarCount(newStarCount);

    setIsLoading(true);

    try {
      const endpoint = `/api/collections/${collectionId}/star`;
      const method = newHasStarred ? "POST" : "DELETE";

      const response = await fetch(endpoint, { method });
      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || "Failed to update star");
      }

      // Success notification (subtle)
      if (newHasStarred) {
        toast.success("Collection starred");
      } else {
        toast.success("Star removed");
      }

      // Notify parent component
      onStarChange?.(newHasStarred);
    } catch (error) {
      // Revert optimistic update on error
      setHasStarred(previousHasStarred);
      setStarCount(previousStarCount);

      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update star. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleStarToggle}
      disabled={isLoading}
      className={cn("gap-1.5", className)}
    >
      <Star
        className={`w-4 h-4 ${
          hasStarred
            ? "fill-yellow-400 stroke-yellow-400"
            : "fill-none stroke-current"
        }`}
      />
      {showCount && (
        <span className="text-sm font-medium">
          {starCount.toLocaleString()}
        </span>
      )}
    </Button>
  );
}

